import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getEmployee, updateEmployee, createFieldFlags, saveReviewProgress, getReviewProgress } from '../services/employeeService';
import { sendReworkRequest } from '../services/notificationService';
import { getViewPresignedUrl } from '../services/fileService';
import { api } from '../services/api';
import { EmployeeRecord, FieldFlag } from '../types';
import { FiArrowLeft, FiCheck, FiX, FiFileText, FiDownload, FiFlag, FiSend, FiMessageSquare, FiEdit3, FiLoader, FiUserPlus, FiSlash } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getStatusNavigationPath } from '../utils/navigationUtils';
import { useAuth } from '../context/AuthContext';

const ReviewSubmissionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employee, setEmployee] = useState<EmployeeRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklist, setChecklist] = useState({
    personalDetails: false,
    residentialAddress: false,
    employmentStatus: false,
    documentsProvided: false,
  });
  const [reviewNotes, setReviewNotes] = useState('');
  const [fieldFlags, setFieldFlags] = useState<FieldFlag[]>([]);
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [reworkReason, setReworkReason] = useState('');
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [currentFlagField, setCurrentFlagField] = useState<string>('');
  const [flagMessage, setFlagMessage] = useState<string>('');
  const [showDocumentRequestModal, setShowDocumentRequestModal] = useState(false);
  const [requestedDocuments, setRequestedDocuments] = useState<string[]>([]);
  const [documentRequestMessage, setDocumentRequestMessage] = useState('');
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [employerFields, setEmployerFields] = useState({
    employeeNumber: '',
    jobGrade: '',
    jobTitle: '',
    department: ''
  });
  const [showEmployerFieldsModal, setShowEmployerFieldsModal] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklistNotes, setBlacklistNotes] = useState('');

  const availableDocuments = [
    'ID',
    'Birth Certificate',
    'Driver\'s License',
    'ITAS Registration Certificate',
    'Social Security Card',
    'Bank Confirmation Letter',
    'Police Clearance Certificate',
    'Proof of Address',
    'CV',
    'Qualifications',
    'Other Certifications'
  ];

  const handleDocumentRequest = () => {
    setShowDocumentRequestModal(true);
    setRequestedDocuments([]);
    setDocumentRequestMessage('');
  };

  const handleDocumentToggle = (docType: string) => {
    setRequestedDocuments(prev => 
      prev.includes(docType) 
        ? prev.filter(d => d !== docType)
        : [...prev, docType]
    );
  };

  const handleSaveDocumentRequest = () => {
    if (requestedDocuments.length === 0) {
      toast.error('Please select at least one document type');
      return;
    }

    const flagMessage = `Additional documents required: ${requestedDocuments.join(', ')}. ${documentRequestMessage}`;
    
    // Create a flag for the attachments section and add it to local flags
    const flag: FieldFlag = {
      fieldPath: 'attachments',
      message: flagMessage,
      severity: 'warning'
    };

    // Add to local field flags state
    setFieldFlags(prev => {
      // Remove any existing attachments flag first
      const filtered = prev.filter(f => f.fieldPath !== 'attachments');
      return [...filtered, flag];
    });

    toast.success('Document request saved. Click "Send Rework Request" to submit all flags.');
    setShowDocumentRequestModal(false);
    
    // Reset the modal state
    setRequestedDocuments([]);
    setDocumentRequestMessage('');
  };

  useEffect(() => {
    if (id) {
      fetchEmployee();
      fetchReviewProgress();
    }
  }, [id]);

  // Re-sync the profile when this tab/page regains focus (e.g. browser back or
  // a bfcache restore after a rework navigation). Without this the action panel
  // can keep showing stale actions — like "Request Rework" on a profile that
  // was already sent back — which then fail server-side with a confusing error.
  useEffect(() => {
    if (!id) return;
    const resync = () => {
      if (document.visibilityState === 'visible') {
        fetchEmployee();
      }
    };
    window.addEventListener('focus', resync);
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('pageshow', resync);
    return () => {
      window.removeEventListener('focus', resync);
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('pageshow', resync);
    };
  }, [id]);

  const fetchReviewProgress = async () => {
    if (!id) return;
    
    try {
      const response = await getReviewProgress(id);
      if (response.data?.reviewProgress) {
        const { checklist, lastSavedAt: savedAt } = response.data.reviewProgress;
        setChecklist(checklist);
        setLastSavedAt(savedAt);
      }
      // Also load review notes if they exist
      if (response.data?.reviewNotes) {
        setReviewNotes(response.data.reviewNotes);
      }
    } catch (error) {
      console.error('Failed to fetch review progress:', error);
      // Don't show error toast as this is optional
    }
  };

  const handleSaveProgress = async () => {
    if (!id || !employee) return;
    
    setIsSavingProgress(true);
    try {
      const response = await saveReviewProgress(id, checklist, reviewNotes);
      setLastSavedAt(response.data.reviewProgress.lastSavedAt);
      
      // Update local employee status if it changed to under_review
      if (response.data.employee && response.data.employee.status !== employee.status) {
        setEmployee(response.data.employee);
      }
      
      toast.success('Review progress saved successfully');
    } catch (error) {
      console.error('Failed to save review progress:', error);
      toast.error('Failed to save review progress');
    } finally {
      setIsSavingProgress(false);
    }
  };

  const fetchEmployee = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const data = await getEmployee(id);
      setEmployee(data);
      
      // Initialize employer fields with current data
      setEmployerFields({
        employeeNumber: data.employeeNumber || '',
        jobGrade: data.jobGrade || '',
        jobTitle: data.jobTitle || '',
        department: data.department || ''
      });
    } catch (error) {
      console.error('Failed to fetch employee:', error);
      toast.error('Failed to load employee data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!employee) return;
    
    // Check if all checklist items are completed
    const allItemsChecked = Object.values(checklist).every(Boolean);
    if (!allItemsChecked) {
      toast.error('Please complete all checklist items before approving');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // First, save the review progress
      await saveReviewProgress(employee.id, checklist, reviewNotes);
      
      // Then update the status to profile_verified
      await updateEmployee(employee.id, { 
        status: 'profile_verified',
        reviewNotes: reviewNotes,
      });
      
      toast.success('Candidate profile verified - now available for hire');
      navigate('/submissions/verified');
    } catch (error) {
      console.error('Error during approval:', error);
      toast.error('Failed to verify candidate profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMakeAvailableForHire = async () => {
    if (!employee) return;
    
    setIsSubmitting(true);
    try {
      await updateEmployee(employee.id, { 
        status: 'available_for_hire',
        reviewNotes: reviewNotes,
      });
      toast.success('Candidate made available for hire');
      navigate('/submissions/verified');
    } catch (error) {
      toast.error('Failed to make candidate available for hire');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHire = async () => {
    if (!employee) return;
    
    setIsSubmitting(true);
    try {
      await updateEmployee(employee.id, { 
        status: 'hired',
        reviewNotes: reviewNotes,
      });
      toast.success('Candidate hired successfully');
      navigate('/submissions/available');
    } catch (error) {
      toast.error('Failed to hire candidate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEmployerFields = async () => {
    if (!employee) return;
    
    setIsSubmitting(true);
    try {
      await updateEmployee(employee.id, {
        employeeNumber: employerFields.employeeNumber,
        jobGrade: employerFields.jobGrade,
        jobTitle: employerFields.jobTitle,
        department: employerFields.department,
      });
      toast.success('Employer fields updated successfully');
      
      // Refresh the employee data to update the UI
      await fetchEmployee();
    } catch (error) {
      toast.error('Failed to update employer fields');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTerminate = async () => {
    if (!employee) return;
    
    setIsSubmitting(true);
    try {
      await updateEmployee(employee.id, { 
        status: 'terminated',
        reviewNotes: reviewNotes,
      });
      toast.success('Employee terminated successfully');
      navigate('/submissions/hired');
    } catch (error) {
      toast.error('Failed to terminate employee');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveToReview = async () => {
    if (!employee) return;

    setIsSubmitting(true);
    try {
      await updateEmployee(employee.id, {
        status: 'under_review',
        reviewNotes: reviewNotes,
      });
      toast.success('Employee moved back to review successfully');
      navigate('/submissions/terminated');
    } catch (error) {
      toast.error('Failed to move employee to review');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlacklist = async (reason: string, notes?: string) => {
    if (!employee) return;

    setIsSubmitting(true);
    try {
      const response = await api.post(`/blacklist/${employee.id}`, {
        reason,
        notes
      });

      toast.success('Candidate blacklisted successfully');
      navigate('/submissions/blacklisted');
    } catch (error: any) {
      console.error('Blacklist error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to blacklist candidate';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadDocument = async (attachment: any) => {
    try {
      // Show loading state
      toast.loading('Preparing document for download...');
      
      // Get presigned URL
      const downloadUrl = await getViewPresignedUrl(attachment.fileKey);
      
      // Open in new tab
      window.open(downloadUrl, '_blank');
      
      toast.dismiss();
      toast.success('Document opened in new tab');
    } catch (error) {
      toast.dismiss();
      console.error('Failed to download document:', error);
      toast.error('Failed to open document');
    }
  };

  const handleReject = async () => {
    if (!employee) return;
    
    setIsSubmitting(true);
    try {
      await updateEmployee(employee.id, { 
        status: 'rejected',
        reviewNotes: reviewNotes,
      });
      toast.success('Candidate rejected');
      navigate('/submissions/rejected');
    } catch (error) {
      toast.error('Failed to reject candidate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestRework = async () => {
    if (!employee || !reworkReason || !Array.isArray(fieldFlags) || fieldFlags.length === 0) {
      toast.error('Please provide a reason and flag at least one field for rework');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Save field flags to database first
      await createFieldFlags(employee.id, fieldFlags);
      
      // Update employee status to requested_rework
      await updateEmployee(employee.id, { 
        status: 'requested_rework',
        reviewNotes: reworkReason,
      });
      
      // Send email notification with rework instructions if email is available
      if (employee.email) {
        await sendReworkRequest(
          employee.id,
          employee.email,
          `${employee.fullNames} ${employee.surname}`,
          reworkReason,
          fieldFlags
        );
        toast.success('Rework request sent to candidate via email');
      } else {
        toast.success('Rework request processed (candidate has no email address)');
      }
      setShowReworkModal(false);
      navigate('/submissions/new');
    } catch (error: any) {
      console.error('Failed to request rework:', error);
      const serverMessage = error?.response?.data?.error;
      const alreadyOutOfReview =
        error?.response?.status === 403 &&
        typeof serverMessage === 'string' &&
        serverMessage.includes('not in review');

      if (alreadyOutOfReview) {
        // The profile already left the review state (e.g. rework was already
        // requested, likely from a stale tab or a double-submit). Tell the user
        // plainly and re-sync so the panel reflects the real status.
        toast.error('Rework has already been requested for this profile.');
        setShowReworkModal(false);
        await fetchEmployee();
      } else {
        toast.error(serverMessage || 'Failed to send rework request');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFlagModal = (fieldPath: string) => {
    // Prevent flagging for profiles not in review
    if (employee?.status !== 'new_submission' && employee?.status !== 'under_review') {
      toast.error('Field flagging is only available for new submissions or profiles under review');
      return;
    }

    // Ensure fieldFlags is an array
    if (!Array.isArray(fieldFlags)) {
      console.warn('fieldFlags is not an array:', fieldFlags);
      setCurrentFlagField(fieldPath);
      setFlagMessage('');
      setShowFlagModal(true);
      return;
    }
    
    const existingFlag = fieldFlags.find(flag => flag.fieldPath === fieldPath);
    setCurrentFlagField(fieldPath);
    setFlagMessage(existingFlag?.message || '');
    setShowFlagModal(true);
  };

  const handleSaveFlag = () => {
    if (!flagMessage.trim()) {
      toast.error('Please provide a message explaining what needs to be fixed');
      return;
    }

    setFieldFlags(prev => {
      const filtered = prev.filter(flag => flag.fieldPath !== currentFlagField);
      return [...filtered, {
        fieldPath: currentFlagField,
        message: flagMessage.trim(),
        severity: 'error' as const
      }];
    });

    setShowFlagModal(false);
    setCurrentFlagField('');
    setFlagMessage('');
    toast.success('Field flagged successfully');
  };

  const removeFlagFromField = (fieldPath: string) => {
    // Prevent removing flags for profiles not in review
    if (employee?.status !== 'new_submission' && employee?.status !== 'under_review') {
      toast.error('Cannot modify flags for profiles not in review');
      return;
    }

    setFieldFlags(prev => prev.filter(flag => flag.fieldPath !== fieldPath));
    toast.success('Flag removed');
  };

  const isFieldFlagged = (fieldPath: string) => {
    // Ensure fieldFlags is an array
    if (!Array.isArray(fieldFlags)) {
      console.warn('fieldFlags is not an array:', fieldFlags);
      return false;
    }
    return fieldFlags.some(flag => flag.fieldPath === fieldPath);
  };

  // Component for individual field flag button
  const FieldFlagButton: React.FC<{ fieldPath: string; inline?: boolean }> = ({ fieldPath, inline = false }) => {
    const isFlagged = isFieldFlagged(fieldPath);
    const isEditable = employee?.status === 'new_submission' || employee?.status === 'under_review';
    
    return (
      <div className={`flex items-center gap-1 ${inline ? 'inline-flex ml-2' : ''}`}>
        {isFlagged && isEditable && (
          <button
            onClick={() => removeFlagFromField(fieldPath)}
            className="p-1 text-orange-600 hover:text-red-600"
            title="Remove flag"
          >
            <FiX size={12} />
          </button>
        )}
        <button
          onClick={() => openFlagModal(fieldPath)}
          disabled={!isEditable}
          className={`p-1 rounded ${isFlagged ? 'text-orange-600 bg-orange-50' : 'text-gray-400 hover:text-orange-600'} ${!isEditable ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={!isEditable ? 'Field flagging only available for new submissions or profiles under review' : (isFlagged ? 'Edit flag message' : 'Flag this field')}
        >
          <FiFlag size={12} />
        </button>
      </div>
    );
  };

  const getChecklistProgress = () => {
    const completed = Object.values(checklist).filter(Boolean).length;
    const total = Object.keys(checklist).length;
    return (completed / total) * 100;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Employee not found</p>
        <Link to="/submissions/new" className="text-blue-600 hover:text-blue-700">
          Back to Submissions
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(getStatusNavigationPath(employee.status))}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <FiArrowLeft />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Review Application: {employee.fullNames} {employee.surname}
            </h1>
            <p className="text-gray-600">Reference: {employee.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(employee.status)}`}>
            {employee.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Personal Details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Full Names
                  <FieldFlagButton fieldPath="fullNames" inline />
                </label>
                <p className="text-gray-900">{employee.fullNames}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Surname
                  <FieldFlagButton fieldPath="surname" inline />
                </label>
                <p className="text-gray-900">{employee.surname}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Email
                  <FieldFlagButton fieldPath="email" inline />
                </label>
                <p className="text-gray-900">{employee.email || 'Not provided'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Contact Number
                  <FieldFlagButton fieldPath="contactNumber" inline />
                </label>
                <p className="text-gray-900">{employee.contactNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Date of Birth
                  <FieldFlagButton fieldPath="dateOfBirth" inline />
                </label>
                <p className="text-gray-900">{new Date(employee.dateOfBirth).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  ID Number
                  <FieldFlagButton fieldPath="idNumber" inline />
                </label>
                <p className="text-gray-900">{employee.idNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Marital Status
                  <FieldFlagButton fieldPath="maritalStatus" inline />
                </label>
                <p className="text-gray-900 capitalize">{employee.maritalStatus}</p>
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Employment Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Position Applied For
                  <FieldFlagButton fieldPath="positionAppliedFor" inline />
                </label>
                <p className="text-gray-900">{Array.isArray(employee.positionAppliedFor) ? employee.positionAppliedFor.join(', ') : employee.positionAppliedFor}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Employment Status
                  <FieldFlagButton fieldPath="employmentStatus" inline />
                </label>
                <p className="text-gray-900">{employee.employmentStatus}</p>
              </div>
              {employee.currentEmployer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Current Employer
                    <FieldFlagButton fieldPath="currentEmployer" inline />
                  </label>
                  <p className="text-gray-900">{employee.currentEmployer}</p>
                </div>
              )}
              {employee.currentPosition && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Current Position
                    <FieldFlagButton fieldPath="currentPosition" inline />
                  </label>
                  <p className="text-gray-900">{employee.currentPosition}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                  Previously Employed by Trigon Mining
                  <FieldFlagButton fieldPath="previouslyEmployedByTrigon" inline />
                </label>
                <p className="text-gray-900">{employee.previouslyEmployedByTrigon}</p>
              </div>
              {employee.previouslyEmployedByTrigon === 'Yes' && employee.previousTrigonPosition && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Previous Position at Trigon
                    <FieldFlagButton fieldPath="previousTrigonPosition" inline />
                  </label>
                  <p className="text-gray-900">{employee.previousTrigonPosition}</p>
                </div>
              )}
              {employee.previouslyEmployedByTrigon === 'Yes' && employee.previousTrigonEmployeeNumber && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Previous Employee Number
                    <FieldFlagButton fieldPath="previousTrigonEmployeeNumber" inline />
                  </label>
                  <p className="text-gray-900">{employee.previousTrigonEmployeeNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Residential Address */}
          {employee.residentialAddress && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Residential Address</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {employee.residentialAddress.streetAddress && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                      Street Address and Number
                      <FieldFlagButton fieldPath="residentialAddress.streetAddress" inline />
                    </label>
                    <p className="text-gray-900">{employee.residentialAddress.streetAddress}</p>
                  </div>
                )}
                {employee.residentialAddress.townCity && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                      Town/City
                      <FieldFlagButton fieldPath="residentialAddress.townCity" inline />
                    </label>
                    <p className="text-gray-900">
                      {employee.residentialAddress.townCity === 'Other' && employee.residentialAddress.customTownCity
                        ? employee.residentialAddress.customTownCity
                        : employee.residentialAddress.townCity}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dependents */}
          {employee.dependents && employee.dependents.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Dependents</h2>
                <div className="flex items-center gap-2">
                  {isFieldFlagged('dependents') && (employee?.status === 'new_submission' || employee?.status === 'under_review') && (
                    <button
                      onClick={() => removeFlagFromField('dependents')}
                      className="p-1 text-orange-600 hover:text-red-600"
                      title="Remove flag"
                    >
                      <FiX size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => openFlagModal('dependents')}
                    disabled={!(employee?.status === 'new_submission' || employee?.status === 'under_review')}
                    className={`p-2 rounded ${isFieldFlagged('dependents') ? 'text-orange-600 bg-orange-50' : 'text-gray-400 hover:text-orange-600'} ${!(employee?.status === 'new_submission' || employee?.status === 'under_review') ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={!(employee?.status === 'new_submission' || employee?.status === 'under_review') ? 'Field flagging only available for new submissions or profiles under review' : (isFieldFlagged('dependents') ? 'Edit flag message' : 'Flag for rework')}
                  >
                    <FiFlag />
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                {employee.dependents.map((dependent, index) => (
                  <div key={index} className="border rounded p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Full Names</label>
                        <p className="text-gray-900">{dependent.fullNames}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Surname</label>
                        <p className="text-gray-900">{dependent.surname}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Relation</label>
                        <p className="text-gray-900">{dependent.relation || 'Not specified'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">ID Number</label>
                        <p className="text-gray-900">{dependent.idNumber || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Employer Input Fields - Only show after hiring */}
          {(['hired', 'current_employee', 'terminated'].includes(employee.status)) && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Employer Input Fields</h2>
                {(employee?.status === 'hired' || employee?.status === 'current_employee') && (
                  <button
                    onClick={() => setShowEmployerFieldsModal(true)}
                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit employer fields"
                  >
                    <FiEdit3 size={16} />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Employee Number
                    {(employee?.status === 'hired' || employee?.status === 'current_employee') && (
                      <FieldFlagButton fieldPath="employeeNumber" inline />
                    )}
                  </label>
                  <p className="text-gray-900">{employee.employeeNumber || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Job Grade
                    {(employee?.status === 'hired' || employee?.status === 'current_employee') && (
                      <FieldFlagButton fieldPath="jobGrade" inline />
                    )}
                  </label>
                  <p className="text-gray-900">{employee.jobGrade || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Job Title
                    {(employee?.status === 'hired' || employee?.status === 'current_employee') && (
                      <FieldFlagButton fieldPath="jobTitle" inline />
                    )}
                  </label>
                  <p className="text-gray-900">{employee.jobTitle || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 flex items-center justify-between">
                    Department
                    {(employee?.status === 'hired' || employee?.status === 'current_employee') && (
                      <FieldFlagButton fieldPath="department" inline />
                    )}
                  </label>
                  <p className="text-gray-900">{employee.department || 'Not set'}</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-green-50 rounded">
                <p className="text-sm text-green-700">
                  <strong>Note:</strong> These fields are filled by HR/Admin staff after the candidate is hired.
                </p>
              </div>
            </div>
          )}
          
          {(['new_submission', 'profile_verified', 'available_for_hire', 'under_review', 'requested_rework'].includes(employee.status)) && (
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Employer Input Fields</h2>
              </div>
              <div className="mt-4 p-4 bg-yellow-50 rounded">
                <p className="text-sm text-yellow-700">
                  <strong>Note:</strong> Employer fields will become available after the candidate is hired.
                </p>
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Uploaded Documents</h2>
              <div className="flex space-x-2">
                <FieldFlagButton fieldPath="attachments" inline />
                {(employee?.status === 'new_submission' || employee?.status === 'under_review') && (
                  <button
                    onClick={handleDocumentRequest}
                    className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors flex items-center"
                  >
                    <FiFileText className="mr-1" size={14} />
                    Request Documents
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-3">
              {employee.attachments && employee.attachments.length > 0 ? (
                employee.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center space-x-2">
                      <FiFileText className="text-green-600" />
                      <div>
                        <span className="font-medium">{attachment.documentType}</span>
                        <p className="text-sm text-gray-600">{attachment.fileName}</p>
                      </div>
                      <FieldFlagButton fieldPath={`attachments.${index}.${attachment.documentType}`} inline />
                    </div>
                    <button 
                      onClick={() => handleDownloadDocument(attachment)}
                      className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50 transition-colors"
                      title="Download document"
                    >
                      <FiDownload />
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-gray-600 text-center py-4">No documents uploaded</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Verification Checklist */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Verification Checklist</h3>
              {(employee?.status === 'new_submission' || employee?.status === 'under_review') && (
                <button
                  onClick={handleSaveProgress}
                  disabled={isSavingProgress}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSavingProgress ? (
                    <>
                      <FiLoader className="animate-spin" size={14} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiCheck size={14} />
                      Save Progress
                    </>
                  )}
                </button>
              )}
            </div>
            {employee?.status !== 'new_submission' && employee?.status !== 'under_review' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Review Complete:</strong> This profile is no longer in review. Review progress cannot be modified.
                </p>
              </div>
            )}
            <div className="space-y-3">
              {Object.entries({
                personalDetails: 'Personal Details',
                residentialAddress: 'Residential Address',
                employmentStatus: 'Employment Status',
                documentsProvided: 'Documents Provided',
              }).map(([key, label]) => (
                <label key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={checklist[key as keyof typeof checklist]}
                    onChange={(e) => setChecklist(prev => ({ ...prev, [key]: e.target.checked }))}
                    disabled={!(employee?.status === 'new_submission' || employee?.status === 'under_review')}
                    className="rounded border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className={`text-sm ${!(employee?.status === 'new_submission' || employee?.status === 'under_review') ? 'text-gray-500' : ''}`}>{label}</span>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Progress</span>
                <span>{Math.round(getChecklistProgress())}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getChecklistProgress()}%` }}
                ></div>
              </div>
              {lastSavedAt && (
                <p className="text-xs text-gray-500 mt-2">
                  Last saved: {new Date(lastSavedAt).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Review Notes */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-bold mb-4">Review Notes</h3>
            {employee?.status !== 'new_submission' && employee?.status !== 'under_review' && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                <p className="text-sm text-blue-800">
                  <strong>Review Complete:</strong> Review notes are read-only as this profile is no longer in review.
                </p>
              </div>
            )}
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Add your review notes here..."
              disabled={!(employee?.status === 'new_submission' || employee?.status === 'under_review')}
              className={`w-full h-32 p-3 border border-gray-300 rounded-lg resize-none ${!(employee?.status === 'new_submission' || employee?.status === 'under_review') ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
            />
          </div>

          {/* Blacklist Information */}
          {employee?.status === 'blacklisted' && employee?.blacklistInfo && (
            <div className="bg-red-50 shadow rounded-lg p-6 border-l-4 border-red-500">
              <h3 className="text-lg font-bold mb-4 text-red-800">Blacklist Information</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-red-700">Reason</label>
                  <p className="text-red-900 font-medium">{employee.blacklistInfo.reason}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-700">Blacklisted By</label>
                  <p className="text-red-900">{employee.blacklistInfo.blacklistedByName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-700">Date Blacklisted</label>
                  <p className="text-red-900">{new Date(employee.blacklistInfo.blacklistedAt).toLocaleDateString()}</p>
                </div>
                {employee.blacklistInfo.notes && (
                  <div>
                    <label className="block text-sm font-medium text-red-700">Additional Notes</label>
                    <p className="text-red-900">{employee.blacklistInfo.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          {user?.role !== 'viewer' ? (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">Actions</h3>
              <div className="space-y-3">
              {employee?.status === 'profile_verified' ? (
                // For verified profiles, only show "Make Available for Hire" and "Reject"
                <>
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Profile Verified:</strong> This profile has been verified. You can only make it available for hire or reject it.
                    </p>
                  </div>
                  <button
                    onClick={handleMakeAvailableForHire}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiUserPlus />}
                    <span>{isSubmitting ? 'Processing...' : 'Make Available for Hire'}</span>
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiX />}
                    <span>{isSubmitting ? 'Rejecting...' : 'Reject'}</span>
                  </button>
                  <button
                    onClick={() => setShowBlacklistModal(true)}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSlash />
                    <span>Blacklist Candidate</span>
                  </button>
                </>
              ) : employee?.status === 'available_for_hire' ? (
                // For available_for_hire, show "Hire" and "Reject"
                <>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg mb-4">
                    <p className="text-sm text-purple-800">
                      <strong>Available for Hire:</strong> This candidate is available for hire. You can hire them or reject them.
                    </p>
                  </div>
                  <button
                    onClick={handleHire}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiCheck />}
                    <span>{isSubmitting ? 'Hiring...' : 'Hire Candidate'}</span>
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiX />}
                    <span>{isSubmitting ? 'Rejecting...' : 'Reject'}</span>
                  </button>
                  <button
                    onClick={() => setShowBlacklistModal(true)}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSlash />
                    <span>Blacklist Candidate</span>
                  </button>
                </>
              ) : (employee?.status === 'hired' || employee?.status === 'current_employee') ? (
                // For hired and current employees, show "Terminate"
                <>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <p className="text-sm text-red-800">
                      <strong>Employee Active:</strong> This employee is currently active. You can terminate their employment if needed.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTerminateModal(true)}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiX />
                    <span>Terminate Employment</span>
                  </button>
                </>
              ) : employee?.status === 'terminated' ? (
                // For terminated employees, show "Move to Review"
                <>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mb-4">
                    <p className="text-sm text-gray-800">
                      <strong>Employee Terminated:</strong> This employee has been terminated. You can move them back to review if needed.
                    </p>
                  </div>
                  <button
                    onClick={handleMoveToReview}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiEdit3 />}
                    <span>{isSubmitting ? 'Moving to Review...' : 'Move to Review'}</span>
                  </button>
                </>
              ) : (employee?.status === 'new_submission' || employee?.status === 'under_review') ? (
                // For new_submission and under_review, show the full set of actions
                <>
                  <button
                    onClick={handleApprove}
                    disabled={isSubmitting || !Object.values(checklist).every(Boolean)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!Object.values(checklist).every(Boolean) ? 'Complete all checklist items to approve' : ''}
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiCheck />}
                    <span>{isSubmitting ? 'Approving...' : 'Approve'}</span>
                  </button>
                  {!Object.values(checklist).every(Boolean) && (
                    <p className="text-xs text-orange-600 text-center mt-1">
                      Complete all checklist items to approve
                    </p>
                  )}
                  <button
                    onClick={() => setShowReworkModal(true)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <FiSend />
                    <span>Request Rework</span>
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiX />}
                    <span>{isSubmitting ? 'Rejecting...' : 'Reject'}</span>
                  </button>
                  <button
                    onClick={() => setShowBlacklistModal(true)}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSlash />
                    <span>Blacklist Candidate</span>
                  </button>
                </>
              ) : employee?.status === 'blacklisted' ? (
                // For blacklisted candidates, only allow moving to review for appeal
                <>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <p className="text-sm text-red-800">
                      <strong>Candidate Blacklisted:</strong> This candidate has been blacklisted and cannot be hired. They can only be moved back to review for appeal.
                    </p>
                  </div>
                  <button
                    onClick={handleMoveToReview}
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? <FiLoader className="animate-spin" /> : <FiEdit3 />}
                    <span>{isSubmitting ? 'Moving to Review...' : 'Move to Review (Appeal)'}</span>
                  </button>
                </>
              ) : (
                // For other statuses (hired, current_employee, etc.), show read-only message
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 text-center">
                    <strong>Review Complete:</strong> This profile is no longer in the review phase. No actions available.
                  </p>
                </div>
              )}
            </div>
          </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">Actions</h3>
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-700 text-center">
                  <strong>View-only Access:</strong> You have read-only access and cannot perform actions on this submission.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rework Modal */}
      {showReworkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Request Rework</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rework *
                </label>
                <textarea
                  value={reworkReason}
                  onChange={(e) => setReworkReason(e.target.value)}
                  placeholder="Explain what needs to be corrected..."
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Flagged Fields ({Array.isArray(fieldFlags) ? fieldFlags.length : 0})
                </label>
                <div className="space-y-2">
                  {Array.isArray(fieldFlags) && fieldFlags.length > 0 ? (
                    fieldFlags.map((flag, index) => (
                      <div key={index} className="p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="font-medium text-orange-800">
                              {flag.fieldPath.charAt(0).toUpperCase() + flag.fieldPath.slice(1).replace(/([A-Z])/g, ' $1')}
                            </span>
                            <p className="text-orange-700 mt-1">{flag.message}</p>
                          </div>
                          <button
                            onClick={() => removeFlagFromField(flag.fieldPath)}
                            className="p-1 text-orange-600 hover:text-red-600 ml-2"
                            title="Remove flag"
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-600">
                      Use the flag buttons next to each section to mark fields that need rework.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowReworkModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestRework}
                disabled={!reworkReason || !Array.isArray(fieldFlags) || fieldFlags.length === 0 || isSubmitting}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? <FiLoader className="animate-spin" size={16} /> : <FiSend size={16} />}
                {isSubmitting ? 'Sending...' : 'Send Rework Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">
                  Flag Field: {currentFlagField.charAt(0).toUpperCase() + currentFlagField.slice(1).replace(/([A-Z])/g, ' $1')}
                </h3>
                <button
                  onClick={() => setShowFlagModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What needs to be fixed? *
                </label>
                <textarea
                  value={flagMessage}
                  onChange={(e) => setFlagMessage(e.target.value)}
                  placeholder="Explain what's wrong and how it should be corrected..."
                  className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none"
                  autoFocus
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowFlagModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFlag}
                  disabled={!flagMessage.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FiFlag size={16} />
                  Save Flag
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Request Modal */}
      {showDocumentRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Request Additional Documents</h3>
                <button
                  onClick={() => setShowDocumentRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select document types to request: *
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 rounded p-3">
                  {availableDocuments.map((docType) => (
                    <label key={docType} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                      <input
                        type="checkbox"
                        checked={requestedDocuments.includes(docType)}
                        onChange={() => handleDocumentToggle(docType)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="text-sm">{docType}</span>
                    </label>
                  ))}
                </div>
                {requestedDocuments.length > 0 && (
                  <div className="mt-2 p-2 bg-orange-50 rounded">
                    <p className="text-sm text-orange-700">
                      <strong>Selected:</strong> {requestedDocuments.join(', ')}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional message (optional)
                </label>
                <textarea
                  value={documentRequestMessage}
                  onChange={(e) => setDocumentRequestMessage(e.target.value)}
                  placeholder="Provide any additional instructions or context for the requested documents..."
                  className="w-full h-20 p-3 border border-gray-300 rounded-lg resize-none"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDocumentRequestModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDocumentRequest}
                  disabled={requestedDocuments.length === 0}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FiFileText size={16} />
                  Save Document Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employer Fields Modal */}
      {showEmployerFieldsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Edit Employer Fields</h3>
                <button
                  onClick={() => setShowEmployerFieldsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Number
                  </label>
                  <input
                    type="text"
                    value={employerFields.employeeNumber}
                    onChange={(e) => setEmployerFields(prev => ({ ...prev, employeeNumber: e.target.value }))}
                    placeholder="Enter employee number"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Grade
                  </label>
                  <input
                    type="text"
                    value={employerFields.jobGrade}
                    onChange={(e) => setEmployerFields(prev => ({ ...prev, jobGrade: e.target.value }))}
                    placeholder="Enter job grade"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={employerFields.jobTitle}
                    onChange={(e) => setEmployerFields(prev => ({ ...prev, jobTitle: e.target.value }))}
                    placeholder="Enter job title"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department
                  </label>
                  <input
                    type="text"
                    value={employerFields.department}
                    onChange={(e) => setEmployerFields(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Enter department"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEmployerFieldsModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await handleSaveEmployerFields();
                    setShowEmployerFieldsModal(false);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <FiLoader className="animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiCheck size={16} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Termination Confirmation Modal */}
      {showTerminateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-red-800">Confirm Termination</h3>
                <button
                  onClick={() => setShowTerminateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <FiX className="text-red-600" size={20} />
                    </div>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      Terminate Employment
                    </h4>
                    <p className="text-sm text-gray-500">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mb-4">
                  Are you sure you want to terminate the employment of <strong>{employee?.fullNames} {employee?.surname}</strong>?
                </p>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> This will change the employee status to "Terminated" and they will no longer be considered an active employee.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowTerminateModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await handleTerminate();
                    setShowTerminateModal(false);
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <FiLoader className="animate-spin" size={16} />
                      Terminating...
                    </>
                  ) : (
                    <>
                      <FiX size={16} />
                      Terminate Employment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blacklist Modal */}
      {showBlacklistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-red-800">Blacklist Candidate</h3>
                <button
                  onClick={() => setShowBlacklistModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-4">
                  Are you sure you want to blacklist <strong>{employee?.fullNames} {employee?.surname}</strong>?
                </p>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> Blacklisted candidates cannot be hired and will be permanently banned from future applications.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for blacklisting *
                    </label>
                    <select
                      value={blacklistReason}
                      onChange={(e) => setBlacklistReason(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    >
                      <option value="">Select a reason</option>
                      <option value="Fraudulent information">Fraudulent information</option>
                      <option value="Failed background check">Failed background check</option>
                      <option value="Inappropriate behavior">Inappropriate behavior</option>
                      <option value="Criminal activity">Criminal activity</option>
                      <option value="Previous termination for cause">Previous termination for cause</option>
                      <option value="Violation of company policies">Violation of company policies</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional notes (optional)
                    </label>
                    <textarea
                      value={blacklistNotes}
                      onChange={(e) => setBlacklistNotes(e.target.value)}
                      placeholder="Provide additional context or details..."
                      className="w-full h-24 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowBlacklistModal(false);
                    setBlacklistReason('');
                    setBlacklistNotes('');
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!blacklistReason) {
                      toast.error('Please select a reason for blacklisting');
                      return;
                    }
                    await handleBlacklist(blacklistReason, blacklistNotes);
                    setShowBlacklistModal(false);
                    setBlacklistReason('');
                    setBlacklistNotes('');
                  }}
                  disabled={isSubmitting || !blacklistReason}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <FiLoader className="animate-spin" size={16} />
                      Blacklisting...
                    </>
                  ) : (
                    <>
                      <FiSlash size={16} />
                      Blacklist Candidate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'new_submission':
      return 'bg-blue-100 text-blue-800';
    case 'candidate':
      return 'bg-yellow-100 text-yellow-800';
    case 'under_review':
      return 'bg-purple-100 text-purple-800';
    case 'requested_rework':
      return 'bg-orange-100 text-orange-800';
    case 'current_employee':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'blacklisted':
      return 'bg-black text-white';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export default ReviewSubmissionPage;
